import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, ListChecks, Mail, CheckCircle2, XCircle, Star, Trash2, Inbox, BarChart3 } from "lucide-react";

function StatCard({ label, value, color = "blue" }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1.5">{label}</div>
      <div className={`text-3xl font-bold text-${color}-600`}>{value}</div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => api.get("/admin/users").then(r => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const verify = async (id, status) => {
    await api.patch(`/admin/users/${id}/verify?status=${status}`);
    toast.success(`User ${status}`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("User deleted");
    load();
  };

  return (
    <div>
      <h2 className="font-bold text-xl mb-4">Users ({users.length})</h2>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Created</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="p-3 font-medium">{u.full_name || u.company_name || "—"}</td>
                  <td className="p-3 text-slate-600">{u.email}</td>
                  <td className="p-3"><span className="dv-pill capitalize">{u.role}</span></td>
                  <td className="p-3">
                    <span className={`dv-pill ${u.verification_status === "verified" ? "bg-emerald-50 text-emerald-700" : u.verification_status === "rejected" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                      {u.verification_status}
                    </span>
                  </td>
                  <td className="p-3 text-slate-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => verify(u.id, "verified")} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="Verify" data-testid={`verify-${u.id}`}><CheckCircle2 size={16} /></button>
                      <button onClick={() => verify(u.id, "rejected")} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Reject"><XCircle size={16} /></button>
                      <button onClick={() => del(u.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ListingsTab() {
  const [listings, setListings] = useState([]);
  const load = useCallback(() => api.get("/admin/listings").then(r => setListings(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, fields) => {
    await api.patch(`/admin/listings/${id}`, fields);
    toast.success("Updated");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/admin/listings/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <h2 className="font-bold text-xl mb-4">Listings ({listings.length})</h2>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr><th className="p-3">Business</th><th className="p-3">Industry</th><th className="p-3">Country</th><th className="p-3">Approved</th><th className="p-3">Featured</th><th className="p-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {listings.map(l => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="p-3 font-medium">{l.business_name}</td>
                  <td className="p-3 text-slate-600">{l.industry}</td>
                  <td className="p-3 text-slate-600">{l.country}</td>
                  <td className="p-3">{l.approved ? <CheckCircle2 size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-slate-400" />}</td>
                  <td className="p-3">{l.featured ? <Star size={16} className="text-amber-500" fill="currentColor" /> : <Star size={16} className="text-slate-300" />}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => patch(l.id, { approved: !l.approved })} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Toggle approved" data-testid={`approve-${l.id}`}><CheckCircle2 size={16} /></button>
                      <button onClick={() => patch(l.id, { featured: !l.featured })} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Toggle featured"><Star size={16} /></button>
                      <button onClick={() => del(l.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InboxTab() {
  const [tab, setTab] = useState("contact");
  const [items, setItems] = useState([]);
  useEffect(() => {
    const url = tab === "contact" ? "/admin/contacts" : tab === "waitlist" ? "/admin/waitlist" : "/admin/contact-requests";
    api.get(url).then(r => setItems(r.data));
  }, [tab]);
  return (
    <div>
      <div className="flex gap-2 mb-5">
        {[{ k: "contact", l: "Contact" }, { k: "waitlist", l: "Waitlist" }, { k: "requests", l: "Listing Requests" }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 rounded-full text-sm font-medium border ${tab === t.k ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>
            {t.l}
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-slate-500 text-center py-12 bg-white rounded-2xl border border-slate-200">No entries.</div>}
        {items.map(i => (
          <div key={i.id} className="bg-white rounded-xl border border-slate-200 p-4 text-sm">
            <div className="flex justify-between items-baseline mb-1">
              <span className="font-semibold">{i.name || i.buyer_name || "Anonymous"}</span>
              <span className="text-xs text-slate-400">{i.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
            <div className="text-slate-600 text-xs mb-1">{i.email || i.buyer_email} {i.country && `· ${i.country}`} {i.role && `· ${i.role}`}</div>
            {i.subject && <div className="font-medium">{i.subject}</div>}
            {(i.message || i.subject) && <div className="text-slate-700">{i.message}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => toast.error("Failed to load stats"));
  }, [user, navigate]);

  if (!user) return null;
  if (user.role !== "admin") return null;

  return (
    <div data-testid="admin-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="mb-6">
        <span className="dv-pill mb-2 bg-amber-50 text-amber-700">Admin</span>
        <h1 className="dv-display text-3xl md:text-4xl">Admin Console</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <StatCard label="Users" value={stats.users_total} />
          <StatCard label="Investors" value={stats.users_by_role?.investor || 0} />
          <StatCard label="Listings" value={stats.listings_total} />
          <StatCard label="Pending approval" value={stats.listings_pending} color="amber" />
          <StatCard label="Featured" value={stats.listings_featured} />
          <StatCard label="Waitlist" value={stats.waitlist_total} />
          <StatCard label="Contact messages" value={stats.contacts_total} />
          <StatCard label="Listing requests" value={stats.contact_requests_total} />
        </div>
      )}

      <Tabs defaultValue="users" className="w-full" data-testid="admin-tabs">
        <TabsList className="bg-white border border-slate-200 rounded-xl p-1 mb-6 overflow-x-auto">
          <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><Users size={14} className="mr-1.5" /> Users</TabsTrigger>
          <TabsTrigger value="listings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><ListChecks size={14} className="mr-1.5" /> Listings</TabsTrigger>
          <TabsTrigger value="inbox" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white rounded-lg px-4 py-2"><Inbox size={14} className="mr-1.5" /> Inbox</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="listings"><ListingsTab /></TabsContent>
        <TabsContent value="inbox"><InboxTab /></TabsContent>
      </Tabs>
    </div>
  );
}
